// src/components/admin/QueryBuilderTab.jsx
// #Revised UI_v2 — QB1: added "Vouchers and Transactions" report type
import React, { useState } from 'react';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore'; // #Revised UI_v2 — QB2
import { db } from '../../utils/firebase';
import { useSalonAuth } from '../../contexts/SalonAuthContext';
import '../../styles/reports.css';

const QueryBuilderTab = () => {
  const [reportType, setReportType] = useState('customers-appointments');
  const [queryConditions, setQueryConditions] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uniqueField, setUniqueField] = useState('');
  const [stylists, setStylists] = useState([]);
  const [services, setServices] = useState([]);

  // Get current user from auth context
  const { currentUser } = useSalonAuth();

  // Fetch stylists and services for dropdown options
  React.useEffect(() => {
    const fetchStylistsAndServices = async () => {
      if (!currentUser?.salonId) return;

      try {
        // Fetch stylists
        const staffRef = collection(db, 'staff');
        const staffQuery = query(staffRef, where('salonId', '==', currentUser.salonId));
        const staffSnapshot = await getDocs(staffQuery);
        const stylistsList = [];
        staffSnapshot.forEach(doc => {
          const data = doc.data();
          stylistsList.push(data.name || data.displayName || '');
        });
        setStylists(stylistsList.filter(name => name)); // Filter out empty names

        // Fetch services
        const servicesRef = collection(db, 'services');
        const servicesQuery = query(servicesRef, where('salonId', '==', currentUser.salonId));
        const servicesSnapshot = await getDocs(servicesQuery);
        const servicesList = [];
        servicesSnapshot.forEach(doc => {
          const data = doc.data();
          servicesList.push(data.name || data.serviceName || '');
        });
        setServices(servicesList.filter(name => name)); // Filter out empty names
      } catch (error) {
        console.error('Error fetching stylists and services:', error);
      }
    };

    fetchStylistsAndServices();
  }, [currentUser?.salonId]);

  // Available report types
  const reportTypes = {
    'customers-appointments': 'Customers and Appointments',
    'customers-transactions': 'Customers and Transactions',
    'customers-vouchers': 'Customers and Vouchers',
    'vouchers-transactions': 'Vouchers and Transactions' // #Revised UI_v2 — QB1
  };

  // Available fields for each report type
  const availableFields = {
    'customers-appointments': {
      customer: [
        'displayName', 'email', 'phoneNumber', 'birthMonth', 'loyaltyTier',
        'totalSpent', 'pointsBalance', 'pointsEarned', 'pointsRedeemed'
      ],
      appointment: [
        'appointmentDate', 'dateTimeCreated', 'serviceName', 'staffName', 'status',
        'checkInStatus', 'price', 'startTime', 'endTime', 'notes', 'customerName'
      ]
    },
    'customers-transactions': {
      customer: [
        'displayName', 'email', 'phoneNumber', 'birthMonth', 'loyaltyTier',
        'totalSpent', 'pointsBalance', 'pointsEarned', 'pointsRedeemed'
      ],
      transaction: [
        'createdAt', 'serviceName', 'paidAt', 'price', 'amount', 'totalAmount',
        'paymentMethod', 'paymentStatus', 'customerName', 'staffName',
        'locationName'
      ]
    },
    'customers-vouchers': {
      customer: [
        'displayName', 'email', 'phoneNumber', 'birthMonth', 'loyaltyTier',
        'totalSpent', 'pointsBalance', 'pointsEarned', 'pointsRedeemed'
      ],
      voucher: [
        'createdAt', 'promotionName', 'description', 'status', 'voucherCode',
        'startDate', 'endDate', 'redeemedAt'
      ]
    },
    'vouchers-transactions': { // #Revised UI_v2 — QB1
      voucher: [
        'voucherCreatedAt', 'promotionName', 'description', 'status', 'voucherCode',
        'startDate', 'endDate', 'redeemedAt'
      ],
      transaction: [
        'createdAt', 'serviceName', 'paidAt', 'price', 'amount', 'totalAmount',
        'paymentMethod', 'paymentStatus', 'customerName', 'staffName',
        'locationName'
      ]
    }
  };

  // Field types for validation
  const fieldTypes = {
    displayName: 'text', email: 'text', phoneNumber: 'text', birthMonth: 'select',
    loyaltyTier: 'select', totalSpent: 'number', pointsBalance: 'number',
    pointsEarned: 'number', pointsRedeemed: 'number', serviceName: 'select',
    price: 'number', amount: 'number', totalAmount: 'number',
    status: 'select', paymentMethod: 'select', paymentStatus: 'select',
    createdAt: 'date', paidAt: 'date', appointmentDate: 'date', voucherCreatedAt: 'date', // #Revised UI_v2 — QB1
    promotionName: 'text', description: 'text', voucherCode: 'text',
    startDate: 'date', endDate: 'date', redeemedAt: 'date', customerName: 'text',
    staffName: 'select', checkInStatus: 'select', startTime: 'text', endTime: 'text', notes: 'text',
    locationName: 'text'
  };

  // Select options for certain fields
  const selectOptions = {
    birthMonth: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    loyaltyTier: ['Bronze', 'Silver', 'Gold', 'Platinum'],
    status: ['pending', 'confirmed', 'completed', 'cancelled', 'approved', 'Active', 'Redeemed', 'Expired'],
    paymentMethod: ['Cash', 'Card', 'PayNow'],
    paymentStatus: ['Pending', 'Completed', 'Failed', 'Refunded'],
    staffName: stylists,
    serviceName: services,
    checkInStatus: ['checked-in', 'not-checked-in', 'no-show']
  };

  // Helper function to safely convert Firestore timestamp to Date
  const parseFirestoreDate = (dateValue) => {
    if (!dateValue) return null;
    
    // If it's already a Date object
    if (dateValue instanceof Date) return dateValue;
    
    // If it's a Firestore timestamp with seconds
    if (dateValue.seconds) {
      return new Date(dateValue.seconds * 1000);
    }
    
    // If it's an ISO string
    if (typeof dateValue === 'string') {
      return new Date(dateValue);
    }
    
    // If it's a timestamp number
    if (typeof dateValue === 'number') {
      // Check if it's in seconds (Unix timestamp) or milliseconds
      if (dateValue < 10000000000) { // Assume seconds if less than this
        return new Date(dateValue * 1000);
      } else {
        return new Date(dateValue);
      }
    }
    
    return null;
  };

  // Fetch customers data from flat collection
  const fetchCustomersData = async () => {
    if (!currentUser?.salonId) {
      throw new Error('No salon ID found');
    }
    
    console.log('Fetching customers for salon:', currentUser.salonId);
    
    try {
      const customersRef = collection(db, 'customers');
      const customersQuery = query(customersRef, where('salonId', '==', currentUser.salonId));
      const customersSnapshot = await getDocs(customersQuery);
      
      const customers = [];
      customersSnapshot.forEach(customerDoc => {
        const customerData = customerDoc.data();
        customers.push({
          id: customerDoc.id,
          ...customerData,
          totalSpent: customerData.metrics?.totalSpent || 0,
          pointsBalance: customerData.metrics?.pointsBalance || 0,
          pointsEarned: customerData.metrics?.pointsEarned || 0,
          pointsRedeemed: customerData.metrics?.pointsRedeemed || 0,
          lastTransaction: customerData.metrics?.lastTransaction
        });
      });
      
      console.log('Found customers:', customers.length);
      return customers;
    } catch (error) {
      console.error('Error fetching customers:', error);
      return [];
    }
  };

  // Fetch transactions from flat collection
  const fetchTransactionsData = async (customersList = []) => { // #Revised UI_v2 — QB2
    if (!currentUser?.salonId) {
      throw new Error('No salon ID found');
    }

    try {
      console.log('Fetching transactions for salon:', currentUser.salonId);
      const transactionsRef = collection(db, 'transactions');
      const transactionsQuery = query(
        transactionsRef,
        where('salonId', '==', currentUser.salonId),
        where('paymentStatus', '==', 'Completed')
      );
      const transactionsSnapshot = await getDocs(transactionsQuery);

      const transactions = [];
      transactionsSnapshot.forEach(txnDoc => {
        const transactionData = txnDoc.data();
        transactions.push({
          id: txnDoc.id,
          ...transactionData,
          // Convert amount from string to number for calculations. Some transactions
          // (e.g. quick walk-in payments) only ever set "price", not amount/totalAmount —
          // #Revised UI_v2 — QB2: fall back to price so those still show a figure.
          amount: parseFloat(transactionData.amount || transactionData.totalAmount || transactionData.price || 0),
          totalAmount: parseFloat(transactionData.totalAmount || transactionData.amount || transactionData.price || 0),
          price: parseFloat(transactionData.price || transactionData.amount || 0)
        });
      });

      // #Revised UI_v2 — QB2: some legacy transactions are missing customerName/locationName.
      // Resolve them here from customers/salons instead of duplicating that data by
      // writing it back onto every transaction document.
      const customersById = {};
      customersList.forEach(c => { customersById[c.id] = c; });

      let salonName = '';
      const locationNameById = {};
      try {
        const salonSnap = await getDoc(doc(db, 'salons', currentUser.salonId));
        if (salonSnap.exists()) {
          const salonData = salonSnap.data();
          salonName = salonData.name || '';
          (salonData.locations || []).forEach(loc => {
            locationNameById[loc.locationId] = loc.locationName;
          });
        }
      } catch (salonError) {
        console.error('Error fetching salon info for report enrichment:', salonError);
      }

      transactions.forEach(t => {
        if (!t.customerName) {
          t.customerName = customersById[t.customerId]?.displayName || t.customerName;
        }
        if (!t.locationName) {
          // Multi-location salons: match by locationId. Single-location salons never
          // set locationId at all, so the salon's own name stands in as "the location".
          t.locationName = (t.locationId && locationNameById[t.locationId]) || salonName || t.locationName;
        }
      });

      console.log('Found completed transactions:', transactions.length);
      return transactions;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      return [];
    }
  };

  // Fetch appointments from flat collection
  const fetchAppointmentsData = async () => {
    if (!currentUser?.salonId) {
      throw new Error('No salon ID found');
    }
    
    try {
      console.log('Fetching appointments for salon:', currentUser.salonId);
      const appointmentsRef = collection(db, 'appointments');
      const appointmentsQuery = query(appointmentsRef, where('salonId', '==', currentUser.salonId));
      const appointmentsSnapshot = await getDocs(appointmentsQuery);
      
      const appointments = [];
      appointmentsSnapshot.forEach(doc => {
        const appointmentData = doc.data();
        appointments.push({
          id: doc.id,
          ...appointmentData,
          // Extract price from serviceRequested if available
          price: appointmentData.serviceRequested?.price || appointmentData.price || 0
        });
      });
      
      console.log('Found appointments:', appointments.length);
      return appointments;
    } catch (error) {
      console.error('Error fetching appointments:', error);
      return [];
    }
  };

  // Fetch vouchers from flat collection
  const fetchVouchersData = async () => {
    if (!currentUser?.salonId) {
      throw new Error('No salon ID found');
    }
    
    try {
      console.log('Fetching vouchers for salon:', currentUser.salonId);
      const vouchersRef = collection(db, 'vouchers');
      const vouchersQuery = query(vouchersRef, where('salonId', '==', currentUser.salonId));
      const vouchersSnapshot = await getDocs(vouchersQuery);
      
      const vouchers = [];
      vouchersSnapshot.forEach(doc => {
        const voucherData = doc.data();
        vouchers.push({
          id: doc.id,
          ...voucherData
        });
      });
      
      console.log('Found vouchers:', vouchers.length);
      return vouchers;
    } catch (error) {
      console.error('Error fetching vouchers:', error);
      return [];
    }
  };

  // Generate report using flat collections
  const generateReport = async () => {
    if (selectedColumns.length === 0) {
      setError('Please select at least one column to display');
      return;
    }

    setLoading(true);
    setError('');
    setReportData([]);
    
    try {
      console.log('Starting report generation...');
      
      const customers = await fetchCustomersData();
      
      if (customers.length === 0) {
        setError('No customers found for your salon. Please check your permissions or add customers first.');
        return;
      }
      
      let reportResults = [];

      if (reportType === 'customers-appointments') {
        const appointments = await fetchAppointmentsData();
        
        appointments.forEach(appointment => {
          const customer = customers.find(c => c.id === appointment.customerId);
          if (customer && matchesConditions({ ...customer, ...appointment })) {
            const filteredRecord = {};
            selectedColumns.forEach(column => {
              filteredRecord[column] = getFieldValue({ ...customer, ...appointment }, column);
            });
            reportResults.push(filteredRecord);
          }
        });
        
      } else if (reportType === 'customers-transactions') {
        const transactions = await fetchTransactionsData(customers); // #Revised UI_v2 — QB2
        
        transactions.forEach(transaction => {
          const customer = customers.find(c => c.id === transaction.customerId);
          if (customer && matchesConditions({ ...customer, ...transaction })) {
            const filteredRecord = {};
            selectedColumns.forEach(column => {
              filteredRecord[column] = getFieldValue({ ...customer, ...transaction }, column);
            });
            reportResults.push(filteredRecord);
          }
        });
        
      } else if (reportType === 'customers-vouchers') {
        const vouchers = await fetchVouchersData();

        vouchers.forEach(voucher => {
          const customer = customers.find(c => c.id === voucher.customerId);
          if (customer && matchesConditions({ ...customer, ...voucher })) {
            const filteredRecord = {};
            selectedColumns.forEach(column => {
              filteredRecord[column] = getFieldValue({ ...customer, ...voucher }, column);
            });
            reportResults.push(filteredRecord);
          }
        });

      } else if (reportType === 'vouchers-transactions') { // #Revised UI_v2 — QB1
        const vouchers = await fetchVouchersData();
        const transactions = await fetchTransactionsData(customers); // #Revised UI_v2 — QB2

        vouchers.forEach(voucher => {
          // Only redeemed vouchers link to the specific transaction they were
          // used against, via redeemedTransactionId (set by redeemVoucher() in
          // salonApi.js). Unredeemed vouchers have no transaction to report.
          if (!voucher.redeemedTransactionId) return;
          const transaction = transactions.find(t => t.id === voucher.redeemedTransactionId);
          if (!transaction) return;

          // Both collections have a raw "createdAt" field; rename the voucher's copy
          // so it doesn't get overwritten by the transaction's when merged below
          const { createdAt: voucherCreatedAt, ...voucherRest } = voucher;
          const merged = { ...voucherRest, voucherCreatedAt, ...transaction };

          if (matchesConditions(merged)) {
            const filteredRecord = {};
            selectedColumns.forEach(column => {
              filteredRecord[column] = getFieldValue(merged, column);
            });
            reportResults.push(filteredRecord);
          }
        });
      }

      // Apply deduplication if unique field is selected
      if (uniqueField && reportResults.length > 0) {
        const uniqueRecords = [];
        const seenValues = new Set();
        
        reportResults.forEach(record => {
          const uniqueValue = record[uniqueField];
          if (!seenValues.has(uniqueValue)) {
            seenValues.add(uniqueValue);
            uniqueRecords.push(record);
          }
        });
        
        reportResults = uniqueRecords;
      }

      setReportData(reportResults);
      
      if (reportResults.length === 0) {
        setError('No data found matching your criteria. Try adjusting your conditions or selecting different columns.');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      setError(`Error generating report: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const matchesConditions = (record) => {
    if (queryConditions.length === 0) return true;

    // Filter out invalid conditions first
    const validConditions = queryConditions.filter(
      condition => condition.field && condition.value !== ''
    );

    if (validConditions.length === 0) return true;

    let result;
    let isFirstCondition = true;

    for (let i = 0; i < validConditions.length; i++) {
      const condition = validConditions[i];
      const fieldValue = getFieldValue(record, condition.field);
      const conditionResult = evaluateCondition(fieldValue, condition.operator, condition.value);

      if (isFirstCondition) {
        // First valid condition sets the initial result
        result = conditionResult;
        isFirstCondition = false;
      } else {
        // For subsequent conditions, use the logical operator from THIS condition
        if (condition.logicalOperator === 'AND') {
          result = result && conditionResult;
        } else {
          result = result || conditionResult;
        }
      }
    }

    return result;
  };

  const getFieldValue = (record, field) => {
    if (field.includes('.')) {
      const parts = field.split('.');
      let value = record;
      for (const part of parts) {
        value = value?.[part];
        if (value === undefined || value === null) break;
      }
      return value;
    }
    return record[field];
  };

  const evaluateCondition = (fieldValue, operator, value) => {
    if (fieldValue === null || fieldValue === undefined) return false;

    // Only try to parse as date if it's actually a date-like value
    const parsedDate = parseFirestoreDate(fieldValue);
    if (parsedDate && !isNaN(parsedDate.getTime())) {
      fieldValue = parsedDate;
    }

    // Only use date comparison if we have a valid Date object
    if (fieldValue instanceof Date && !isNaN(fieldValue.getTime()) && value) {
      const compareDate = new Date(value);
      // Also check if the compare date is valid
      if (!isNaN(compareDate.getTime())) {
        switch (operator) {
          case 'equals':
            return fieldValue.toDateString() === compareDate.toDateString();
          case 'greater-than':
            return fieldValue > compareDate;
          case 'less-than':
            return fieldValue < compareDate;
          case 'greater-equal':
            return fieldValue >= compareDate;
          case 'less-equal':
            return fieldValue <= compareDate;
        }
      }
    }

    if (typeof fieldValue === 'number' || !isNaN(Number(fieldValue))) {
      const numFieldValue = Number(fieldValue);
      const numValue = Number(value);
      
      switch (operator) {
        case 'equals':
          return numFieldValue === numValue;
        case 'not-equals':
          return numFieldValue !== numValue;
        case 'greater-than':
          return numFieldValue > numValue;
        case 'less-than':
          return numFieldValue < numValue;
        case 'greater-equal':
          return numFieldValue >= numValue;
        case 'less-equal':
          return numFieldValue <= numValue;
      }
    }

    const strFieldValue = String(fieldValue).toLowerCase();
    const strValue = String(value).toLowerCase();

    switch (operator) {
      case 'equals':
        return strFieldValue === strValue;
      case 'not-equals':
        return strFieldValue !== strValue;
      case 'contains':
        return strFieldValue.includes(strValue);
      default:
        return false;
    }
  };

  // Add new condition
  const addCondition = () => {
    setQueryConditions([...queryConditions, {
      id: Date.now(),
      field: '',
      operator: 'equals',
      value: '',
      logicalOperator: 'AND'
    }]);
  };

  // Remove condition
  const removeCondition = (id) => {
    setQueryConditions(queryConditions.filter(condition => condition.id !== id));
  };

  // Update condition
  const updateCondition = (id, updates) => {
    setQueryConditions(queryConditions.map(condition => 
      condition.id === id ? { ...condition, ...updates } : condition
    ));
  };

  // Reset report builder
  const resetReportBuilder = () => {
    setQueryConditions([]);
    setSelectedColumns([]);
    setUniqueField('');
    setReportData([]);
    setError('');
  };

  // #Revised UI_v2 — QB2: each report type has its own field set, so a column (or
  // condition) selected under one type is invalid under another and would render
  // as a blank/stale column if left in place after switching
  const handleReportTypeChange = (newReportType) => {
    setReportType(newReportType);
    resetReportBuilder();
  };

  // Clear unique field when columns change
  const toggleColumn = (field) => {
    setSelectedColumns(prev => {
      const newColumns = prev.includes(field) 
        ? prev.filter(col => col !== field)
        : [...prev, field];
      
      // Clear unique field if it's no longer in selected columns
      if (uniqueField && !newColumns.includes(uniqueField)) {
        setUniqueField('');
      }
      
      return newColumns;
    });
  };

  const exportToCSV = () => {
    if (reportData.length === 0) return;

    const headers = selectedColumns.map(col => col.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()));
    const csvContent = [
      headers.join(','),
      ...reportData.map(record =>
        selectedColumns.map(col => {
          const value = record[col];

          // Only try to parse as date if the field type is actually a date
          if (fieldTypes[col] === 'date' && value) {
            const parsedDate = parseFirestoreDate(value);
            if (parsedDate && !isNaN(parsedDate.getTime())) {
              return parsedDate.toLocaleDateString();
            }
          }

          if (typeof value === 'string' && value.includes(',')) {
            return `"${value}"`;
          }
          // #Revised UI_v2 — QB2: "value || ''" treated 0/false as blank; only null/undefined should be
          return value === null || value === undefined ? '' : value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${reportType}-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="query-builder-section">
      <div className="reports-header">
        <h1>Query Builder</h1>
        <p>Build custom reports with advanced filtering</p>
      </div>

      <div className="query-controls">
        <div className="control-group">
          <h3>Report Type</h3>
          <select
            value={reportType}
            onChange={(e) => handleReportTypeChange(e.target.value)}
            className="report-type-select"
          >
            {Object.entries(reportTypes).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <h3>Query Conditions</h3>
          <button onClick={addCondition} className="add-condition-btn">
            + Add Condition
          </button>
          
          <div className="conditions-list">
            {queryConditions.map((condition, index) => (
              <div key={condition.id} className="condition-item">
                {index > 0 && (
                  <select 
                    value={condition.logicalOperator}
                    onChange={(e) => updateCondition(condition.id, { logicalOperator: e.target.value })}
                    className="logical-operator"
                  >
                    <option value="AND">AND</option>
                    <option value="OR">OR</option>
                  </select>
                )}
                
                <select 
                  value={condition.field}
                  onChange={(e) => updateCondition(condition.id, { field: e.target.value })}
                  className="field-select"
                >
                  <option value="">Select Field</option>
                  {availableFields[reportType] && Object.entries(availableFields[reportType]).map(([category, fields]) => (
                    <optgroup key={category} label={category.charAt(0).toUpperCase() + category.slice(1)}>
                      {fields.map(field => (
                        <option key={field} value={field}>{field}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                
                <select 
                  value={condition.operator}
                  onChange={(e) => updateCondition(condition.id, { operator: e.target.value })}
                  className="operator-select"
                >
                  <option value="equals">Equals</option>
                  <option value="not-equals">Not Equals</option>
                  <option value="contains">Contains</option>
                  <option value="greater-than">Greater Than</option>
                  <option value="less-than">Less Than</option>
                  <option value="greater-equal">Greater or Equal</option>
                  <option value="less-equal">Less or Equal</option>
                </select>
                
                {fieldTypes[condition.field] === 'select' ? (
                  <select 
                    value={condition.value}
                    onChange={(e) => updateCondition(condition.id, { value: e.target.value })}
                    className="value-input"
                  >
                    <option value="">Select Value</option>
                    {selectOptions[condition.field]?.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                ) : (
                  <input 
                    type={fieldTypes[condition.field] || 'text'}
                    value={condition.value}
                    onChange={(e) => updateCondition(condition.id, { value: e.target.value })}
                    placeholder="Value"
                    className="value-input"
                  />
                )}
                
                <button 
                  onClick={() => removeCondition(condition.id)}
                  className="remove-condition-btn"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="control-group">
          <h3>Select Columns</h3>
          {availableFields[reportType] && Object.entries(availableFields[reportType]).map(([category, fields]) => (
            <div key={category} className="field-category">
              <h4>{category.charAt(0).toUpperCase() + category.slice(1)}</h4>
              <div className="field-checkboxes">
                {fields.map(field => (
                  <label key={field} className="checkbox-label">
                    <input 
                      type="checkbox"
                      checked={selectedColumns.includes(field)}
                      onChange={() => toggleColumn(field)}
                    />
                    <span>{field}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="control-group">
          <h3>Unique Records</h3>
          <p style={{color: '#666', fontSize: '14px', marginBottom: '12px'}}>
            Select a field to ensure unique records (removes duplicates based on this field)
          </p>
          <select 
            value={uniqueField} 
            onChange={(e) => setUniqueField(e.target.value)}
            className="unique-field-select"
          >
            <option value="">No deduplication (show all records)</option>
            {selectedColumns.map(field => (
              <option key={field} value={field}>{field}</option>
            ))}
          </select>
          {uniqueField && (
            <p style={{color: '#0070c9', fontSize: '12px', marginTop: '8px'}}>
              Records will be deduplicated based on "{uniqueField}" field
            </p>
          )}
        </div>

        <div className="control-group">
          <button 
            onClick={generateReport}
            disabled={loading || selectedColumns.length === 0}
            className="generate-report-btn"
          >
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
          
          <button 
            onClick={resetReportBuilder}
            className="reset-report-btn"
            style={{ marginLeft: '12px' }}
          >
            Reset All
          </button>
          
          {selectedColumns.length === 0 && (
            <p style={{color: '#666', fontSize: '14px', marginTop: '8px'}}>
              Please select at least one column to generate a report
            </p>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message" style={{
          background: '#ffebee', 
          color: '#d32f2f', 
          padding: '12px', 
          borderRadius: '6px', 
          margin: '20px 0'
        }}>
          {error}
        </div>
      )}

      {reportData.length > 0 && (
        <div className="report-results">
          <div className="results-header">
            <div className="results-title">
              <h3>Report Results ({reportData.length} records)</h3>
              {uniqueField && (
                <p className="deduplication-notice">
                  Deduplicated by "{uniqueField}" field
                </p>
              )}
            </div>
            <div className="results-actions">
              <button onClick={exportToCSV} className="export-btn">
                Export CSV
              </button>
              <button onClick={() => window.print()} className="print-btn">
                Print
              </button>
            </div>
          </div>

          <div className="results-table-container">
            <table className="results-table">
              <thead>
                <tr>
                  {selectedColumns.map(column => (
                    <th key={column}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportData.map((record, index) => (
                  <tr key={index}>
                    {selectedColumns.map(column => (
                      <td key={column}>
                        {(() => {
                          const value = record[column];

                          // Only try to parse as date if the field type is actually a date
                          if (fieldTypes[column] === 'date' && value) {
                            const parsedDate = parseFirestoreDate(value);
                            if (parsedDate && !isNaN(parsedDate.getTime())) {
                              return parsedDate.toLocaleDateString();
                            }
                          }

                          if (typeof value === 'object' && value !== null) {
                            return JSON.stringify(value);
                          }
                          // #Revised UI_v2 — QB2: "value || ''" treated 0/false as blank; only null/undefined should be
                          return value === null || value === undefined ? '' : String(value);
                        })()}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading && <div className="loading-message">Generating report...</div>}
    </div>
  );
};

export default QueryBuilderTab;